import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Edit, Eye, Filter, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { Tooltip } from '../../components/ui/Tooltip';
import { ColumnCustomizer } from '../../components/ColumnCustomizer';
import { PermissionGate } from '../../components/PermissionGate';
import { CourseImportModal } from '../../components/training/CourseImportModal';
import { CourseDeleteModal } from '../../components/training/CourseDeleteModal';
import { useFilters } from '../../hooks/useFilters';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { usePractices } from '../../hooks/usePractices';
import { trainingCoursesStore } from '../../services/trainingCoursesStore';
import { linkedPractices, practiceMembers } from '../../utils/practices';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { courseEconomics } from '../../utils/courseEconomics';
import { formatEuro } from '../../utils/currency';
import { trainingProducts } from '../../data/trainingProducts';
import { courseCharacteristics, courseCommissions, courseTabs, courseVenues } from '../../data/trainingCourseOptions';
import { commissionLabel, getTrainedCourses, getTrainingProduct, parseCourseStatus, venueLabel } from '../../utils/trainingCourses';
import { CourseStatus, TrainingCourse, TrainingCourseDraft } from '../../types/trainingCourse';
const columnsConfig = [{
  key: 'courseCode',
  label: 'Codice corso'
}, {
  key: 'name',
  label: 'Nome corso'
}, {
  key: 'productCode',
  label: 'Tipologia · Codice prodotto'
}, {
  key: 'characteristic',
  label: 'Caratteristica'
}, {
  key: 'venue',
  label: 'Sede'
}, {
  key: 'commission',
  label: 'Commessa'
}, {
  key: 'participants',
  label: 'Partecipanti'
}, {
  key: 'actions',
  label: 'Azioni'
}, {
  key: 'margin',
  label: 'Margine'
}];
const onDemandHiddenColumns = ['venue', 'commission'];
const characteristicVariant = {
  Unico: 'neutral',
  Trainante: 'primary',
  Trainato: 'info'
} as const;
export function CoursesPage() {
  const navigate = useNavigate();
  const courses = useTrainingCourses();
  const {
    practices
  } = usePractices();
  const billing = useTrainingBilling();
  // Margine = ricavi − costi, as in the course dashboard
  const marginOf = (course: TrainingCourse) => courseEconomics(course, practices, billing).margin;
  // Total course users: participants of all practices assigned to the course
  // (on-demand courses: the single participants who bought the course)
  const participantsTotal = (course: TrainingCourse) => course.status === 'ondemand' ? (course.enrollments || []).length : linkedPractices(practices, course.courseCode).reduce((sum, p) => sum + practiceMembers(p).length, 0);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseCourseStatus(searchParams.get('tab'));
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [deletingCourse, setDeletingCourse] = useState<TrainingCourse | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const tabCourses = useMemo(() => courses.filter(c => c.status === activeTab), [courses, activeTab]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData,
    activeFilterCount
  } = useFilters(tabCourses);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filteredData, ['name', 'courseCode', 'productCode', 'clientName']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, activeTab]);
  const openCreate = () => navigate(`/training/corsi/nuovo?stato=${activeTab}`);
  const openView = (course: TrainingCourse) => navigate(`/training/corsi/${course.id}`);
  const openEdit = (course: TrainingCourse) => navigate(`/training/corsi/${course.id}/modifica`);
  const handleImport = (drafts: TrainingCourseDraft[]) => trainingCoursesStore.importMany(drafts);
  const handleDelete = (course: TrainingCourse) => trainingCoursesStore.remove(course.id);
  const handleExport = () => {
    const header = ['Codice corso', 'Nome corso', 'Tipologia', 'Prodotto', 'Codice prodotto', 'Acquisto prodotto esterno', 'Caratteristica', ...(isOnDemand ? [] : ['Sede', 'Commessa']), 'Partecipanti', 'Margine'];
    const rows = searchResult.map(c => [c.courseCode, c.name, getTrainingProduct(c.productCode)?.type || '', getTrainingProduct(c.productCode)?.name || '', c.productCode, c.externalProduct ? 'Sì' : 'No', c.characteristic, ...(isOnDemand ? [] : [venueLabel(c.venue), commissionLabel(c)]), participantsTotal(c), marginOf(c).toFixed(2).replace('.', ',')]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `corsi-${activeTab}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const isOnDemand = activeTab === 'ondemand';
  // Sede and Commessa do not apply to on-demand courses
  const tabColumns = isOnDemand ? columnsConfig.filter(c => !onDemandHiddenColumns.includes(c.key)) : columnsConfig;
  const show = (key: string) => visibleColumns.includes(key) && tabColumns.some(c => c.key === key);
  const shownColumns = tabColumns.filter(c => visibleColumns.includes(c.key));
  const handleTabChange = (tab: CourseStatus) => {
    if (tab === 'ondemand') {
      setFilter('venue', '');
      setFilter('commission', '');
    }
    setSearchParams({
      tab
    }, {
      replace: true
    });
  };
  return <Layout data-id="element-6228" data-mp="2lccbz6d9n3oh">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-6229" data-mp="2ld5c3ohpyuxn">
        <div data-id="element-6230" data-mp="2gfc2elh7jism">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6231" data-mp="2gcpx6shshfdk">Corsi</h1>
          <p className="text-slate-500 mt-1" data-id="element-6232" data-mp="r05y1zszm8sh">
            Creazione e gestione dei corsi di formazione.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-6233" data-mp="m2w2e2f090w7">
          <Button variant="flat" color="secondary" startContent={<Upload className="h-4 w-4" data-id="element-6235" data-mp="32nhio8g1aogp" />} onClick={() => setIsImportOpen(true)} data-id="element-6234" data-mp="2xnmtqaftk8ya">
            Import massivo
          </Button>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-6237" data-mp="2svlhgoinii89" />} onClick={handleExport} data-id="element-6236" data-mp="2nxtjbcrexzvm">
            Esporta
          </Button>
          <PermissionGate module="training" action="create" data-id="element-6238" data-mp="2ezriax7oj78c">
            <Button onClick={openCreate} startContent={<Plus className="h-4 w-4" data-id="element-6240" data-mp="2500qj3ai76by" />} data-id="element-6239" data-mp="2j0jwm4ves2bh">
              Nuovo corso
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="bg-slate-100 p-1 rounded-xl flex items-center w-fit max-w-full overflow-x-auto mb-6" role="tablist" aria-label="Stato dei corsi" data-id="element-6241" data-mp="2a0eqrz5v5bzf">
        {courseTabs.map(tab => {
        const count = courses.filter(c => c.status === tab.id).length;
        const isActive = activeTab === tab.id;
        return <button key={tab.id} type="button" role="tab" aria-selected={isActive} onClick={() => handleTabChange(tab.id)} className={`px-4 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors duration-150 ${isActive ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6242" data-mp="2n56ixl3y599u">
              {tab.label}
              <span className={`text-xs tabular-nums ${isActive ? 'text-seform-orange' : 'text-slate-400'}`} data-id="element-6243" data-mp="1zdsrdhdmdhpx">
                {count}
              </span>
            </button>;
      })}
      </div>

      <Card className="mb-6" noPadding data-id="element-6244" data-mp="3trtnvvlp5smu">
        <div className="p-4 flex flex-col gap-4" data-id="element-6245" data-mp="3ot8xenw8ynbl">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6246" data-mp="3ouk0smls5t26">
            <div className="flex-1" data-id="element-6247" data-mp="7ngobpurtcq">
              <Input placeholder="Cerca nome corso, codice corso, codice prodotto, azienda..." startContent={<Search className="h-4 w-4" data-id="element-6249" data-mp="54dqwiez7uq8" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-6248" data-mp="6kl3qhl52qh" />
            </div>
            <div className="flex gap-2" data-id="element-6250" data-mp="ey30bm2met8s">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6252" data-mp="3nbk35zy88gil" />} data-id="element-6251" data-mp="3dep51i5v8i4u">
                Filtri{' '}
                {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6253" data-mp="3rf89ygf1b82s">
                    {activeFilterCount}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={tabColumns} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training-courses" lockedColumns={['name']} data-id="element-6254" data-mp="16mo947fo5tek" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6255" data-mp="10m7hswt4s3zh">
              <Select label="Tipologia di corso" value={filters.productCode || ''} onChange={e => setFilter('productCode', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...trainingProducts.map(p => ({
            value: p.code,
            label: `${p.type} — ${p.name}`
          }))]} data-id="element-6256" data-mp="15mlcze9mvjg3" />
              <Select label="Caratteristica" value={filters.characteristic || ''} onChange={e => setFilter('characteristic', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...courseCharacteristics.map(c => ({
            value: c,
            label: c
          }))]} data-id="element-6257" data-mp="1t9bf0flzfe0p" />
              {!isOnDemand && <>
                  <Select label="Sede" value={filters.venue || ''} onChange={e => setFilter('venue', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, ...courseVenues]} data-id="element-6258" data-mp="38z2k7a4vo6x2" />
                  <Select label="Commessa" value={filters.commission || ''} onChange={e => setFilter('commission', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, ...courseCommissions]} data-id="element-6259" data-mp="2v0cn3ui2678p" />
                </>}
              <div className="col-span-full flex justify-end" data-id="element-6260" data-mp="jcmv4qf1wjqv">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6262" data-mp="23u5mzkdzey83" />} data-id="element-6261" data-mp="2ds1xc9z3hp55">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6263" data-mp="2witfo1128pvo">
        <div className="overflow-x-auto" data-id="element-6264" data-mp="2wk4hvweuxpko">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6265" data-mp="2wgyqgairabls">
            <thead data-id="element-6266" data-mp="31h4p4ang0ydd">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6267" data-mp="31fjuym51jhg1">
                {shownColumns.map(col => <th key={col.key} className={`px-4 py-4 whitespace-nowrap ${col.key === 'actions' ? 'pr-6' : ''} ${col.key === 'participants' ? 'text-center' : ''} ${col.key === 'margin' ? 'text-right pr-6' : ''}`} data-id="element-6268" data-mp="2rltartfy5t65">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6269" data-mp="3pfc4djkxwiz6">
              {paginatedData.length === 0 && <tr data-id="element-6270" data-mp="75a8zjui3d2f">
                  <td colSpan={shownColumns.length} className="px-4 py-16 text-center text-sm text-slate-500" data-id="element-6271" data-mp="36aw9d2j2c0f">
                    {tabCourses.length === 0 ? 'Nessun corso in questa sezione.' : 'Nessun corso corrisponde alla ricerca o ai filtri.'}
                  </td>
                </tr>}
              {paginatedData.map(course => {
              const product = getTrainingProduct(course.productCode);
              const trainedCount = getTrainedCourses(courses, course.id).length;
              return <tr key={course.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-6272" data-mp="2nvh0kufwubp4">
                    {show('courseCode') && <td className="px-4 py-4 text-sm font-mono text-slate-600 whitespace-nowrap" data-id="element-6273" data-mp="35lcfbk69k2k1">
                        {course.courseCode}
                      </td>}
                    {show('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-6274" data-mp="3fcguokzuppfe">
                        {course.name || '-'}
                        {course.externalProduct && <span className="block text-xs font-normal text-slate-500 mt-0.5" data-id="element-6275" data-mp="3o98zyi07flz7">
                            Acquisto prodotto esterno
                          </span>}
                      </td>}
                    {show('productCode') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6276" data-mp="1yxyrvuju8nb9">
                        {product?.type || '-'}
                        <span className="block text-xs text-slate-400 mt-0.5" data-id="element-6277" data-mp="23x1xbg3qcvjr">
                          {product?.name && `${product.name} · `}
                          <span className="font-mono" data-id="element-6278" data-mp="1rjy7csax0xzu">{course.productCode}</span>
                        </span>
                      </td>}
                    {show('characteristic') && <td className="px-4 py-4" data-id="element-6279" data-mp="1ho4lujf7xu5x">
                        <Badge variant={characteristicVariant[course.characteristic]} size="sm" data-id="element-6280" data-mp="2fak2xk3qgpsy">
                          {course.characteristic}
                          {course.characteristic === 'Trainante' && ` (${trainedCount})`}
                        </Badge>
                      </td>}
                    {show('venue') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6281" data-mp="tysdflrptq4o">
                        {venueLabel(course.venue)}
                      </td>}
                    {show('commission') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-6282" data-mp="3d1ytsvmkcmna">
                        {commissionLabel(course) || '-'}
                      </td>}
                    {show('participants') && <td className="px-4 py-4 text-sm text-slate-700 text-center tabular-nums" data-id="element-6283" data-mp="384qbmv1f0im1">
                        {participantsTotal(course)}
                      </td>}
                    {show('actions') && <td className="px-4 py-4 pr-6" data-id="element-6284" data-mp="2y7lzzp5745jh">
                        <div className="flex items-center gap-1" data-id="element-6285" data-mp="2kfe21u26e8j7">
                          <Tooltip content="Visualizza corso" data-id="element-6286" data-mp="2kh8q3h8qugri">
                            <Button variant="light" size="sm" className="!px-1" aria-label="Visualizza corso" onClick={() => openView(course)} data-id="element-6287" data-mp="2pfk1vxiln1cd">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-6288" data-mp="1vv18z5m20tw8" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="training" action="edit" data-id="element-6289" data-mp="25sfg2em386bv">
                            <Tooltip content="Modifica corso" data-id="element-6290" data-mp="25qma71jatq4x">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Modifica corso" onClick={() => openEdit(course)} data-id="element-6291" data-mp="20sugq3309vym">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-6292" data-mp="2ol178lm1v8q4" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="training" action="delete" data-id="element-6293" data-mp="2yf9zr9b2z1oa">
                            <Tooltip content="Elimina corso" data-id="element-6294" data-mp="2tjun2kot7btg">
                              <Button variant="light" size="sm" className="!px-1" aria-label="Elimina corso" onClick={() => setDeletingCourse(course)} data-id="element-6295" data-mp="1e6qlhorlmtbj">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-6296" data-mp="1t2galvs2zmkh" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                    {show('margin') && <td className={`px-4 py-4 pr-6 text-sm font-medium text-right tabular-nums whitespace-nowrap ${marginOf(course) < 0 ? 'text-red-600' : 'text-green-700'}`} data-id="element-6297" data-mp="15bkcyi0y1x5b">
                        {formatEuro(marginOf(course))}
                      </td>}
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6298" data-mp="2qx0jy3p1bn1k">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6299" data-mp="2qxil9eqk0rgc" />
        </div>
      </Card>

      <CourseImportModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} status={activeTab} onImport={handleImport} data-id="element-6300" data-mp="2c09qeb21l5jv" />
      <CourseDeleteModal course={deletingCourse} trainedCount={deletingCourse ? getTrainedCourses(courses, deletingCourse.id).length : 0} onClose={() => setDeletingCourse(null)} onConfirm={handleDelete} data-id="element-6301" data-mp="236n030glsui2" />
    </Layout>;
}